import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { forgotPasswordSchema, type ForgotPasswordInput } from '@rutatec/shared-schemas'
import { api } from '@/lib/api'

export const ForgotPasswordPage: React.FC = () => {
  const [sent, setSent] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const mutation = useMutation({
    mutationFn: async (data: ForgotPasswordInput) => {
      await api.post('/auth/forgot-password', data)
    },
    onSuccess: () => setSent(true),
  })

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-dark">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-modal">
        <div className="mb-6 text-center">
          <h1 className="font-display text-4xl font-bold">
            <span className="text-navy">RUTA</span><span className="text-red">TEC</span>
          </h1>
        </div>

        {sent ? (
          <div className="text-center">
            <div className="mb-4 text-4xl">📧</div>
            <h2 className="mb-2 font-display text-xl font-bold text-navy">Email enviado</h2>
            <p className="mb-6 font-body text-sm text-gray-DEFAULT">
              Revisá tu bandeja de entrada. El enlace para restablecer tu contraseña
              expira en 1 hora.
            </p>
            <Link to="/login" className="font-body text-sm font-semibold text-navy hover:underline">
              Volver al inicio de sesión
            </Link>
          </div>
        ) : (
          <>
            <h2 className="mb-2 font-display text-2xl font-bold text-navy">
              Recuperar contraseña
            </h2>
            <p className="mb-6 font-body text-sm text-gray-DEFAULT">
              Ingresá tu email y te enviaremos un enlace para restablecer tu contraseña.
            </p>

            <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
              <div>
                <label className="mb-1 block font-body text-sm font-medium text-gray-dark">
                  Email
                </label>
                <input
                  {...register('email')}
                  type="email"
                  className="w-full rounded-lg border border-gray-silver px-4 py-2.5 font-body text-sm
                             focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red">{errors.email.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={mutation.isPending}
                className="w-full rounded-lg bg-navy py-3 font-body font-semibold text-white
                           hover:bg-navy-dark disabled:opacity-60 transition-colors"
              >
                {mutation.isPending ? 'Enviando...' : 'Enviar enlace'}
              </button>
            </form>

            <div className="mt-4 text-center">
              <Link to="/login" className="font-body text-sm text-gray-DEFAULT hover:text-navy">
                ← Volver al inicio de sesión
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
